import React, { useEffect, useState } from "react";
import { API } from "aws-amplify";

import {
  Box,
  Grid,
  InputAdornment,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  AutocompleteInput,
  Button,
  FormDataConsumer,
  minValue,
  number,
  NumberInput,
  required,
  SelectInput,
  TextInput,
} from "react-admin";
import { useForm } from "react-final-form";

import { CalculateIcon } from "../../../components/common/iconography";
import { CustomFields } from "../../../components/common/CustomFields";
import { ColorInput } from "../../../components/common/ColorInput";

import {
  InventoryField,
  Unit,
  UnitOptions,
  UnitToColumn,
  UnitWeightOptions,
} from "../../../utils/constants";
import {
  calculateLf,
  calculateMbf,
  calculateMsf,
  getDefaultUOM,
  getUnitLabel,
  isEngineeredLumberCommodity,
  isInventoryFieldVisible,
  isLumberCommodity,
  isPanelsCommodity,
  isPieceDimensionsVisible,
  unitOptionRenderer,
} from "../../../utils/loadTypeUtils";
import { setupCommodityMaps } from "../../../utils/appUtils";
import { getReloadYardCommodities } from "../../../graphql/reloadyard/custom-queries";
import { validateProductCodeUnicity } from "./ProductUtils";

const validateRequired = required();
const validateUOM = (value) => number()(value) || minValue(0)(value);
const validateGreaterThanZero = (value) =>
  Number(value) > 0 ? undefined : "Must be greater than 0";
const validateUomRequired = (value) =>
  required()(value) || number()(value) || validateGreaterThanZero(value);
const isUomRequired = (allValues, unit) => allValues?.defaultUOM === unit.id;
// allValues.defaultWeightUOM === unit.id   weight is optional

const validateRequiredNumber = [required(), number(), minValue(0)];
const validateNumber = [number(), minValue(0)];
const validateProductCode = () => (value, allValues, meta) => {
  // meta sat on saveValidate and onBlur with mutators.setFieldData
  const metaError = meta?.data?.error;
  if (metaError) return metaError;

  const validators = [required()];
  for (const v of validators) {
    const err = v(value, allValues, meta);
    if (err) return err;
  }
  return undefined;
};

const getCalculateButton = (form, formData) => {
  const isLumber = isLumberCommodity(formData?.reloadYardCommodityId);
  const isPanels = isPanelsCommodity(formData?.reloadYardCommodityId);
  const isEngineeredLumber = isEngineeredLumberCommodity(
    formData?.reloadYardCommodityId,
  );

  if (!isLumber && !isPanels && !isEngineeredLumber) {
    return null;
  }

  let label = "";

  if (isLumber) {
    label = "Calculate MBF";
  } else if (isPanels) {
    label = "Calculate MSF";
  } else if (isEngineeredLumber) {
    label = "Calculate LF";
  }

  let isDisabled = false;
  let disabledReason = "";

  if (isLumber) {
    const missing = [];
    if (!formData?.pcs) missing.push("PCS");
    if (!formData?.pcsWidth || !formData?.pcsHeight || !formData?.pcsLength)
      missing.push("Piece Dimensions");

    isDisabled = missing.length > 0;
    if (isDisabled) {
      disabledReason = `Missing ${missing.join(", ")}.`;
    }
  } else if (isPanels) {
    const missing = [];
    if (!formData?.pcs) missing.push("PCS");
    if (!formData?.pcsWidth || !formData?.pcsLength)
      missing.push("Piece Dimensions");

    isDisabled = missing.length > 0;
    if (isDisabled) {
      disabledReason = `Missing ${missing.join(", ")}.`;
    }
  } else if (isEngineeredLumber) {
    const missing = [];
    if (!formData?.pcs) missing.push("PCS");
    if (!formData?.pcsLength) missing.push("Piece Length");

    isDisabled = missing.length > 0;
    if (isDisabled) {
      disabledReason = `Missing ${missing.join(", ")}.`;
    }
  }

  const onClick = () => {
    let value = null;
    let field = null;
    if (isLumber) {
      value = calculateMbf(formData);
      field = UnitToColumn[Unit.MBF];
    } else if (isPanels) {
      value = calculateMsf(formData);
      field = UnitToColumn[Unit.MSF];
    } else if (isEngineeredLumber) {
      value = calculateLf(formData);
      field = UnitToColumn[Unit.LF];
    }
    form.change(field, value);
  };

  return (
    <Grid item xs={12} md={3}>
      <Box mt={1}>
        {isDisabled ? (
          <Tooltip title={disabledReason}>
            <span>
              <Button label={label} size="large" variant="outlined" disabled>
                <CalculateIcon />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            label={label}
            size="large"
            variant="outlined"
            onClick={onClick}
          >
            <CalculateIcon />
          </Button>
        )}
      </Box>
    </Grid>
  );
};

const getValidUOM = ({ yardCommodities, reloadYardCommodityId }) => {
  const validUom = yardCommodities?.find(
    ({ id }) => id === reloadYardCommodityId,
  )?.commodity?.validUom;
  if (!validUom || !validUom.length) {
    return UnitOptions;
  }
  return UnitOptions.filter(({ id }) => validUom.includes(id));
};

const ensureDefaultUnitValue = (form, defaultUOM) => {
  if (!defaultUOM) return;

  const unitColumn = UnitToColumn[defaultUOM];
  if (!unitColumn) return;

  const currentValue = form.getFieldState(unitColumn)?.value;
  if (!currentValue) form.change(unitColumn, 1);
};

const handleDefaultUOMChange = (form, event) => {
  const value = event?.target?.value;
  if (value) {
    form.change("defaultUOM", value);
    ensureDefaultUnitValue(form, value);
  }
};

const handleLoadTypeChange = (form, value) => {
  const defaultUOM = getDefaultUOM(value);

  if (defaultUOM) {
    form.change("defaultUOM", defaultUOM);
    ensureDefaultUnitValue(form, defaultUOM);
  }

  if (isLumberCommodity(value)) {
    form.change(UnitToColumn[Unit.MSF], null);
    form.change(UnitToColumn[Unit.LF], null);
  } else if (isPanelsCommodity(value)) {
    form.change("pcsLength", null);
    form.change(UnitToColumn[Unit.MBF], null);
    form.change(UnitToColumn[Unit.LF], null);
  } else if (isEngineeredLumberCommodity(value)) {
    form.change("pcsHeight", null);
    form.change("pcsWidth", null);
    form.change(UnitToColumn[Unit.MBF], null);
    form.change(UnitToColumn[Unit.MSF], null);
  }
};

export const ProductForm = ({ account }) => {
  const form = useForm();
  // Bump to force a re-render after refreshing the cached commodity metadata.
  const [, setCommodityVersion] = useState(0);
  const yardCommodities = window.appUser.reloadYard.activeYardCommodities.items;

  // Load-type gating (piece dimensions, valid UOM) is derived from
  // window.appUser.reloadYard.yardCommodityMap, which is hydrated once at login.
  // A long-lived session can hold commodity records that predate schema additions
  // (validUom / inventoryFields), so the gated fields never appear until a full
  // page reload. Re-fetch the yard's commodity metadata on mount and rebuild the
  // maps so the form responds to load-type changes without a manual refresh.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await API.graphql({
          query: getReloadYardCommodities,
          variables: { id: window.appUser.reloadYardId },
        });
        const items = res?.data?.getReloadYard?.yardCommodities?.items;
        if (active && items) {
          window.appUser.reloadYard.yardCommodities = { items };
          setupCommodityMaps(window.appUser.reloadYard);
          setCommodityVersion((v) => v + 1);
        }
      } catch (err) {
        // Non-fatal: fall back to the existing cached commodity metadata.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <FormDataConsumer>
      {({ formData }) => {
        const loadTypeValidUOM = getValidUOM({
          yardCommodities,
          reloadYardCommodityId: formData?.reloadYardCommodityId,
        });
        const unitEntries = loadTypeValidUOM.filter(
          (e) => e.id !== Unit.CAR && e.id !== Unit.TL,
        );
        return (
          <Grid container item direction="column" spacing={1} lg={12}>
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="name"
                  label="Product Name"
                  variant="outlined"
                  fullWidth
                  validate={validateRequired}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextInput
                  source="productCode"
                  label="Product Code"
                  variant="outlined"
                  fullWidth
                  parse={(v) => (v !== "" ? v.toUpperCase() : undefined)}
                  validate={validateProductCode()}
                  onBlur={async () => {
                    await validateProductCodeUnicity(form);
                  }}
                  helperText={false}
                />
              </Grid>
              <Grid item xs={false} md={3}>
                <ColorInput
                  source="color"
                  label="Product Color"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} md={3}>
                <AutocompleteInput
                  source="reloadYardCommodityId"
                  label="Load Type"
                  variant="outlined"
                  fullWidth
                  choices={yardCommodities ? yardCommodities : []}
                  optionText="commodity.name"
                  optionValue="id"
                  validate={validateRequired}
                  onChange={(value) => handleLoadTypeChange(form, value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextInput
                  source="category"
                  label="Commodity Category"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <NumberInput
                  source="lowInventoryThreshold"
                  label="Low Inventory Alert"
                  min="0"
                  defaultValue="0"
                  validate={validateRequiredNumber}
                  placeholder="0"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
            {/* <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="textSecondary">
                    Account Overrides
                  </Typography>
                </legend>
                <Grid container direction="row" spacing={1}>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="defaultWeightUOM"
                      label="Default Weight Units"
                      variant="outlined"
                      disabled
                      fullWidth
                      choices={UnitWeightOptions}
                      optionText={unitOptionRenderer}
                      allowEmpty
                    />
                  </Grid>
                </Grid>
              </fieldset>
            </Grid> */}

            <Grid item xs={12}>
              <fieldset>
                <legend>
                  <Typography variant="overline" color="textSecondary">
                    UNIT CONVERSION MATRIX
                  </Typography>
                </legend>

                <Grid container direction="row" spacing={1}>
                  <Grid item xs={12} md={3}>
                    <SelectInput
                      source="defaultUOM"
                      label="Units of Measure"
                      variant="outlined"
                      fullWidth
                      choices={loadTypeValidUOM}
                      optionText={unitOptionRenderer}
                      validate={validateRequired}
                      onChange={(event) => handleDefaultUOMChange(form, event)}
                    />
                  </Grid>
                </Grid>

                <Grid container direction="row" spacing={1}>
                  {unitEntries.map((unit) => (
                    <Grid item xs={12} md={3}>
                      <NumberInput
                        source={unit.column}
                        label={getUnitLabel(unit.id)}
                        variant="outlined"
                        fullWidth
                        placeholder="0"
                        min="0"
                        // format={(v) => (Number(v) === 0 ? undefined : v)}
                        // parse={(v) => (v === "0" ? undefined : v)}
                        validate={(value, allValues) =>
                          isUomRequired(allValues, unit)
                            ? validateUomRequired(value)
                            : validateUOM(value)
                        }
                        required={isUomRequired(formData, unit)}
                        InputProps={{ inputMode: "numeric" }}
                        // InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </fieldset>
            </Grid>

            {isPieceDimensionsVisible(formData?.reloadYardCommodityId) && (
              <Grid container direction="row" spacing={1}>
                <Grid item xs={12}>
                  <Typography
                    variant="overline"
                    color="textSecondary"
                    gutterBottom
                  >
                    PIECE DIMENSIONS
                  </Typography>
                </Grid>
                {isInventoryFieldVisible(
                  InventoryField.HEIGHT,
                  formData?.reloadYardCommodityId,
                ) && (
                  <Grid item xs={12} md={3}>
                    <NumberInput
                      source="pcsHeight"
                      label="Piece Height"
                      variant="outlined"
                      fullWidth
                      placeholder="0.00"
                      min="0"
                      validate={validateNumber}
                      InputProps={{
                        inputMode: "decimal",
                        endAdornment: (
                          <InputAdornment position="end">IN</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}

                {isInventoryFieldVisible(
                  InventoryField.WIDTH,
                  formData?.reloadYardCommodityId,
                ) && (
                  <Grid item xs={12} md={3}>
                    <NumberInput
                      source="pcsWidth"
                      label="Piece Width"
                      variant="outlined"
                      fullWidth
                      placeholder="0.00"
                      min="0"
                      validate={validateNumber}
                      InputProps={{
                        inputMode: "decimal",
                        endAdornment: (
                          <InputAdornment position="end">IN</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
                {isInventoryFieldVisible(
                  InventoryField.LENGTH,
                  formData?.reloadYardCommodityId,
                ) && (
                  <Grid item xs={12} md={3}>
                    <NumberInput
                      source="pcsLength"
                      label="Piece Length"
                      variant="outlined"
                      fullWidth
                      placeholder="0.00"
                      min="0"
                      validate={validateNumber}
                      InputProps={{
                        inputMode: "decimal",
                        endAdornment: (
                          <InputAdornment position="end">FT</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
                {getCalculateButton(form, formData)}
              </Grid>
            )}

            <Grid container direction="row" spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  color="textSecondary"
                  gutterBottom
                >
                  UNIT DIMENSIONS
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <NumberInput
                  source="height"
                  label="Height"
                  variant="outlined"
                  fullWidth
                  placeholder="0.00"
                  min="0"
                  validate={validateNumber}
                  InputProps={{
                    inputMode: "decimal",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <NumberInput
                  source="width"
                  label="Width"
                  variant="outlined"
                  fullWidth
                  placeholder="0.00"
                  min="0"
                  validate={validateNumber}
                  InputProps={{
                    inputMode: "decimal",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <NumberInput
                  source="length"
                  label="Length"
                  variant="outlined"
                  fullWidth
                  placeholder="0.00"
                  min="0"
                  validate={validateNumber}
                  InputProps={{
                    inputMode: "decimal",
                  }}
                />
              </Grid>
            </Grid>

            <CustomFields
              recordFields={formData.customFields}
              accountFields={account?.inventoryCustomFields}
              header={true}
              width={3}
            />
          </Grid>
        );
      }}
    </FormDataConsumer>
  );
};
